import { Box, Flex, Popover, type PopoverProps, Text } from '@mantine/core'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import { useCallback, useMemo, type FC } from 'react'

import {
  type ParameterID,
  type CoreProduct,
  type CoreProductSelection,
} from '@flecto/types'

import { AvatarItem, BadgeProductInsurance } from '..'
import classes from './styles.module.css'

export type PopoverProductBundleItemsParams = PopoverProps & {
  product: CoreProduct
  withInsuranceBadge?: boolean
  onItemClick?: (itemId: ParameterID) => void
  itemsSelectionMap?: Map<ParameterID, CoreProductSelection>
  opened?: boolean
  onChange?: (opened: boolean) => void
}
export const PopoverProductBundleItems: FC<PopoverProductBundleItemsParams> = ({
  product,
  children,
  onItemClick,
  withInsuranceBadge,
  opened,
  onChange,
  ...props
}) => {
  if (!product?.bundle) return <>{children}</>

  const router = useRouter()

  const isFlectoLink = router.asPath.startsWith('/b_') ?? false

  const shouldShowInsuranceBadge = !!withInsuranceBadge && product.insured

  const handleItemClick = useCallback(
    (itemId: ParameterID) => {
      if (onItemClick) {
        onItemClick(itemId)
      } else {
        window.open(`/products/item/${itemId}`, '_blank')
      }
      onChange?.(false)
    },
    [onItemClick, onChange]
  )

  const renderBundleItems = useMemo(() => {
    return product.bundle_attributes?.bundle_items?.map((bi) => (
      <Box
        key={bi.id}
        className={clsx(classes.item, {
          [classes.noHover]: isFlectoLink,
        })}
        onClick={(e) => {
          e.stopPropagation()
          if (!isFlectoLink) {
            handleItemClick(bi.item.internal_id)
          }
        }}
        data-testid='popover-item'
      >
        <AvatarItem item={bi.item} className={classes.avatar} />
        <Flex className={classes.title}>
          <Text fw={500} truncate title={bi.item.title}>
            {bi.item.title}
          </Text>
          {shouldShowInsuranceBadge && (
            <BadgeProductInsurance
              data-testid='badge-product-insurance'
              product={product}
            />
          )}
        </Flex>
        <Text size='sm' c='dimmed' className={classes.id}>
          ID: {bi.item.internal_id}
        </Text>
        <Text className={classes.quantity} ta='right'>
          x{bi.quantity}
        </Text>
      </Box>
    ))
  }, [
    product.bundle_attributes?.bundle_items,
    shouldShowInsuranceBadge,
    product,
    handleItemClick,
  ])

  return (
    <Popover
      position='bottom'
      width={330}
      withinPortal
      trapFocus={false}
      closeOnClickOutside
      opened={opened}
      onChange={onChange}
      {...props}
    >
      <Popover.Target>
        <Box data-testid='popover-target' className={classes.target}>
          {children}
        </Box>
      </Popover.Target>

      <Popover.Dropdown data-testid='popover-dropdown'>
        {renderBundleItems}
      </Popover.Dropdown>
    </Popover>
  )
}
